import { Component, OnInit } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { GenericBadgeRendererComponent } from 'src/app/shared/common/generic-badge-renderer.component';
import { AgGridActionbutton } from 'src/app/shared/common/AgGridActionbutton';
import { JSIGNALBUTTON } from 'src/app/shared/common/JsignalButtons';
import { ActionDialogComponent } from '../action-dialog/action-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DeviationComponent } from '../../deviation/deviation.component';
import { ActionStatusChangeDialogComponent } from '../action-status-change-dialog/action-status-change-dialog.component';
import { JsignalService } from 'src/app/shared/services/jsignal.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LocalStorageSettingsService } from 'src/app/shared/services/local-storage-settings.service';
import { UserRoutingModule } from '../../user-routing/user-routing.module';
import { Router } from '@angular/router';

@Component({
    selector: 'app-actions',
    templateUrl: './actions.component.html',
    styleUrls: ['./actions.component.scss'],
    standalone: false
})
export class ActionsComponent implements OnInit {

  totalActions: number = 0;
  openCount: number = 0;
  completedCount: number = 0;
  overdueCount: number = 0;

  selectedTab: 'MY' | 'TEAM' | 'CALENDAR' = 'MY';
  myActionsCount = 0;
  currentUserId: string = '';

  readonly DISABLED_STATES = ['Completed', 'Rejected', 'Cancelled'];

  dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  currentDate = new Date();
  currentMonth = this.currentDate.getMonth();
  currentYear = this.currentDate.getFullYear();
  calendar: any[] = [];
  todayDate = this.currentDate.getDate();
  todayMonth = this.currentMonth;
  todayYear = this.currentYear;
  todayEvents: any[] = [];
  calendarEvents: any[] = [];

  allActions: any[] = [];
  mydata: any[] = [];
  teamsData: any[] = [];

  myColumnDefs: any[] = [];
  teamColumnDefs: any[] = [];
  defaultColDef: any;
  gridOptions: any;
  components: any;
  rowClassRules: any;

  gridApi: any;
  gridColumnApi: any;

  constructor(
    private dialog: MatDialog,
    private jsignalService: JsignalService,
    private spinner: NgxUiLoaderService,
    private snackBar: MatSnackBar,
    private localStorage: LocalStorageSettingsService,
    private userRoutingModule: UserRoutingModule,
    private router: Router,
    
    
  ) {
    this.components = {
      jsignalButton: JSIGNALBUTTON,
      buttonRenderer: AgGridActionbutton,
      badgeRenderer: GenericBadgeRendererComponent
    };
  }

  ngOnInit(): void {
    this.currentUserId = this.localStorage.getSettings("UserId");

    if (!this.currentUserId) {
      this.snackBar.open('User session not found. Showing all actions in Team tab.', 'OK', { duration: 4000 });
    }

    this.generateCalendar();
    this.initializeGrids();
    this.loadActions();
  }

  // ================= API METHODS =================

  loadActions(): void {
    this.spinner.start();

    this.jsignalService.getActions().subscribe({
      next: (response: any) => {
        this.spinner.stop();

        let actions: any[] = [];
        if (Array.isArray(response)) {
          actions = response;
        } else if (response?.data) {
          actions = response.data;
        }

        this.allActions = actions.map(action => this.mapActionForDisplay(action));
        this.filterActionsByUser();
        this.updateSummaryCounts();
        this.generateCalendarEvents(actions);
      },
      error: (error) => {
        this.spinner.stop();
        this.snackBar.open(
          error.error?.statusMessage || error.error?.message || 'Failed to load actions',
          'OK',
          { duration: 3000 }
        );
        console.error(error);
      }
    });
  }

  isActionDisabled(state: string): boolean {
    return this.DISABLED_STATES.includes(state);
  }

  filterActionsByUser(): void {
    if (!this.allActions || this.allActions.length === 0) {
      this.mydata = [];
      this.teamsData = [];
      this.myActionsCount = 0;
      return;
    }

    if (!this.currentUserId) {
      // If no user ID, all actions go to Team tab
      this.teamsData = [...this.allActions];
      this.mydata = [];
      this.myActionsCount = 0;
      return;
    }

    // My Actions: Only actions assigned to current user
    this.mydata = this.allActions.filter(action =>
      action.assignedUserId && action.assignedUserId.toString() === this.currentUserId
    );

    // Team Actions: ALL actions (including those assigned to current user)
    this.teamsData = [...this.allActions];

    this.myActionsCount = this.mydata.length;
  }

 mapActionForDisplay(action: any): any {
  return {
    id: action.id,
    actionId: action.actionId,
    actionName: action.actionName,
    originatingFrom: action.originatingFrom,
    deviationId: action.deviationId,
    deviationCode: action.deviationCode,        // ADD THIS
    deviationState: action.deviationState,      // ADD THIS
    deviationClosureDate: action.deviationClosureDate, // ADD THIS
    priority: action.priority,
    status: action.status,
    state: action.state,
    dueDate: action.dueDate,
    actionDescription: action.actionDescription,
    reasonForCancellation: action.reasonForCancellation,
    actionOutcome: action.actionOutcome,
    externalId: action.externalId,
    assignedUserId: action.assignedUserId,
    assignedUserName: action.assignedUserName || 'Unassigned',
    createdBy: action.createdBy,
    createdDate: action.createdDate,
    lastModifiedBy: action.lastModifiedBy,
    lastModifiedDate: action.lastModifiedDate,
    displayStatus: action.status === 'Yes' ? 'Active' : 'Inactive',
    isActive: action.status === 'Yes',
    displayState: action.state,
    isDisabled: this.isActionDisabled(action.state)
  };
}

  updateSummaryCounts(): void {
    this.totalActions = this.allActions.length;

    this.openCount = this.allActions.filter(item =>
      ['On-going', 'IN_PROGRESS', 'PENDING_REVIEW'].includes(item.state)
    ).length;

    this.completedCount = this.allActions.filter(item =>
      item.state === 'Completed'
    ).length;

    this.overdueCount = this.allActions.filter(item =>
      this.isOverdue(item)
    ).length;
  }

  generateCalendarEvents(actions: any[]): void {
    this.calendarEvents = actions
      .filter(action => !!action.dueDate)
      .map(action => ({
        id: action.id,
        actionId: action.actionId,
        title: action.actionName,
        date: new Date(action.dueDate),
        type: this.isOverdue(action) ? 'overdue' : 'open',
        link: `/actions/${action.actionId}`,
        priority: action.priority,
        state: action.state
      }));
  }

  // ================= AG GRID SETUP =================
  initializeGrids() {
    const requiredColumns = [
      {
        headerName: 'Action ID',
        field: 'actionId',
        minWidth: 180,
        pinned: 'left',
        wrapHeaderText: true,
        autoHeaderHeight: true,
        ...this.jsignalService.autoTooltip("Action ID"),
      },
      {
        headerName: 'Action Name',
        field: 'actionName',
        minWidth: 220,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        ...this.jsignalService.autoTooltip("Action Name")
      },
      {
        headerName: 'Originating From',
        field: 'originatingFrom',
        minWidth: 240,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        ...this.jsignalService.autoTooltip("Originating From")
      },
      {
        headerName: 'Priority',
        field: 'priority',
        minWidth: 140,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        ...this.jsignalService.autoTooltip("Priority")
      },
      {
        headerName: 'State',
        field: 'state',
        minWidth: 180,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        ...this.jsignalService.autoTooltip("State")
      },
      {
        headerName: 'Status',
        field: 'status',
        minWidth: 120,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        ...this.jsignalService.autoTooltip("Status"),
        cellRenderer: GenericBadgeRendererComponent,
              cellRendererParams: { type: 'YES_NO' }

      },
      {
        headerName: 'Due Date',
        field: 'dueDate',
        minWidth: 180,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        ...this.jsignalService.autoTooltip("Due Date"),
        valueFormatter: (params: any) => params.value ? new Date(params.value).toLocaleDateString() : ''
      },
      {
        headerName: 'Assigned To',
        field: 'assignedUserName',
        minWidth: 200,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        ...this.jsignalService.autoTooltip("Assigned To"),
      },
      
      {
        headerName: 'Action Outcome',
        field: 'actionOutcome',
        minWidth: 180,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        ...this.jsignalService.autoTooltip("Action Outcome")
      },
      {
        headerName: 'Reason',
        field: 'reasonForCancellation',
        minWidth: 180,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        ...this.jsignalService.autoTooltip("Reason for Cancellation")
      },
      {
      headerName: 'Deviation ID',
      field: 'deviationId',
      minWidth: 180,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      ...this.jsignalService.autoTooltip("Deviation ID"),
   cellRenderer: 'jsignalButton',
        cellRendererParams: {
    deviationLink: true,
    getDeviationUrl: (params) => this.getDeviationUrl(params)
  }
    },
    // ================= NEW COLUMNS ADDED =================
    {
      headerName: 'Deviation Code',
      field: 'deviationCode',
      minWidth: 200,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      ...this.jsignalService.autoTooltip("Deviation Code"),
    },
    {
      headerName: 'Deviation State',
      field: 'deviationState',
      minWidth: 180,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      ...this.jsignalService.autoTooltip("Deviation State"),
    },
    {
      headerName: 'Deviation Closure Date',
      field: 'deviationClosureDate',
      minWidth: 200,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      ...this.jsignalService.autoTooltip("Deviation Closure Date"),
      valueFormatter: (params: any) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString();
      }
    }
    ];

    this.myColumnDefs = [
      ...requiredColumns,
      {
        headerName: 'Actions',
        field: 'actions',
        minWidth: 250,
        pinned: 'right',
        suppressSizeToFit: true,
        cellRenderer: 'buttonRenderer',
        cellRendererParams: (params: any) => {
          const row = params.data;
          const isOverdue = this.isOverdue(row);
          const isDisabled = this.isActionDisabled(row.state);

          return {
            showEdit: !isDisabled,
            showDelete: !isDisabled,
            showDeviation: true,
            showStatusChange: !isDisabled,
            deviationDisabled: !isOverdue || isDisabled,
            edit: () => this.onEditRow(row, 'MY'),
            delete: () => this.onDeleteRow(row, 'MY'),
            deviation: () => this.onDeviationCreation(row),
            statusChange: () => this.openStatusChangeDialog(row)
          };
        }
      }
    ];

    this.teamColumnDefs = [
      ...requiredColumns,
      {
        headerName: 'Actions',
        field: 'actions',
        minWidth: 250,
        pinned: 'right',
        suppressSizeToFit: true,
        cellRenderer: 'buttonRenderer',
        cellRendererParams: (params: any) => {
          const row = params.data;
          const isOverdue = this.isOverdue(row);
          const isDisabled = this.isActionDisabled(row.state);

          return {
            showEdit: !isDisabled,
            showDelete: !isDisabled,
            showAssign: !isDisabled,
            showDeviation: true,
            deviationDisabled: !isOverdue || isDisabled,
            edit: () => this.onEditRow(row, 'TEAM'),
            delete: () => this.onDeleteRow(row, 'TEAM'),
            userAssign: () => this.onUserAssign(row),
            deviation: () => this.onDeviationCreation(row),
          };
        }
      }
    ];

    this.defaultColDef = {
filterParams: {
        maxNumConditions: 2,
        numAlwaysVisibleConditions: 2,
        defaultJoinOperator: 'OR'
      },
      sortable: true,
      filter: true,
      resizable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      autoHeight: true
    };

    this.gridOptions = {
      pagination: true,
      paginationPageSize: 10,
    };

    this.rowClassRules = {
      'critical-row': (params: any) => params.data.priority?.toLowerCase() === 'critical',
      'high-row': (params: any) => params.data.priority?.toLowerCase() === 'high',
      'overdue-row': (params: any) => this.isOverdue(params.data),
      'disabled-row': (params: any) => this.isActionDisabled(params.data.state)
    };
  }

  // ================= ACTION METHODS =================

  onCreate(): void {
    const dialogRef = this.dialog.open(ActionDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      disableClose: true,
      panelClass: 'action-dialog-panel',
      data: { mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadActions();
    });
  }

  onEditRow(row: any, tabType: 'MY' | 'TEAM'): void {
     if (row.status === 'No') {
      this.snackBar.open(`Action ${row.actionId} is already deleted`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }
    if (this.isActionDisabled(row.state)) {
      this.snackBar.open(`Cannot edit action in ${row.state} state`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    const dialogRef = this.dialog.open(ActionDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      disableClose: true,
      panelClass: 'action-dialog-panel',
      data: { action: row, mode: 'edit', tabType }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadActions();
    });
  }

  // FIXED: Properly handle backend response for delete
  onDeleteRow(row: any, tabType: 'MY' | 'TEAM'): void {
    // Check if action is already deleted (status = "No")
    if (row.status === 'No') {
      this.snackBar.open(`Action ${row.actionId} is already deleted`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (this.isActionDisabled(row.state)) {
      this.snackBar.open(`Cannot delete action in ${row.state} state`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (!confirm(`Are you sure you want to delete Action: ${row.actionId}?`)) return;

    this.spinner.start();
    this.jsignalService.deleteAction(row.id).subscribe({
      next: (response: any) => {
        this.spinner.stop();
        
        const message = response?.statusMessage || response?.message || 'Action deleted successfully';
        const isError = response?.error === true;
        
        if (isError) {
          this.snackBar.open(message, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
        } else {
          this.snackBar.open(message, 'OK', { duration: 3000 });
          this.loadActions(); // Reload only on success
        }
      },
      error: (error) => {
        this.spinner.stop();
        // FIXED: Handle error response properly
        const errorMessage = error.error?.statusMessage || 
                            error.error?.message || 
                            error.message || 
                            'Failed to delete action';
        this.snackBar.open(errorMessage, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
        console.error('Delete error:', error);
      }
    });
  }

  openStatusChangeDialog(row: any): void {
     if (row.status === 'No') {
      this.snackBar.open(`Action ${row.actionId} is already deleted, cannot change status`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }
    if (this.isActionDisabled(row.state)) {
      this.snackBar.open(`Cannot change status of action in ${row.state} state`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    const dialogRef = this.dialog.open(ActionStatusChangeDialogComponent, {
      width: '500px',
      disableClose: true,
      data: { action: row }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadActions();
    });
  }

  onDeviationCreation(row: any): void {

     if (row.status === 'No') {
      this.snackBar.open(`Action ${row.actionId} is already deleted, cannot create deviation`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }
    if (this.isActionDisabled(row.state)) {
      this.snackBar.open(`Cannot create deviation for action in ${row.state} state`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }
    if (row.deviationState === 'COMPLETED') {
        this.snackBar.open('Deviation is already completed.','OK',{ duration: 5000 });
        return;
    }

    this.dialog.open(DeviationComponent, {
      maxWidth: "100vw",
      height: "97%",
      width: "90%",
      disableClose: true,
      data: {
        uniqueRecordId: row.id,
        deviationId: row.deviationId, 
        deviationFor: 'ACTION'
      }
    });
  }

  // FIXED: Handle assign user response properly
  onUserAssign(row: any): void {
     if (row.status === 'No') {
      this.snackBar.open(`Action ${row.actionId} is already deleted, cannot assign user`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }
    if (this.isActionDisabled(row.state)) {
      this.snackBar.open(`Cannot assign user to action in ${row.state} state`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    const assignedUser = this.localStorage.getSettings("UserId");

    if (!assignedUser) {
      this.snackBar.open('No user ID found in settings', 'OK', { duration: 3000 });
      return;
    }

    if (row.assignedUserId && row.assignedUserId.toString() === assignedUser) {
      this.snackBar.open('This action is already assigned to you', 'OK', { duration: 3000 });
      return;
    }

    this.spinner.start();
    this.jsignalService.assignUserToAction(row.id, assignedUser).subscribe({
      next: (response: any) => {
        this.spinner.stop();
        // FIXED: Handle response with statusMessage field
        const message = response?.statusMessage || response?.message || 'User assigned successfully';
        const isError = response?.error === true;
        
        if (isError) {
          this.snackBar.open(message, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
        } else {
          this.snackBar.open(message, 'OK', { duration: 3000 });
          this.loadActions();
        }
      },
      error: (error) => {
        this.spinner.stop();
        const errorMessage = error.error?.statusMessage || 
                            error.error?.message || 
                            error.message || 
                            'Failed to assign user';
        this.snackBar.open(errorMessage, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
        console.error('Assignment error:', error);
      }
    });
  }

  // ================= UTILITY METHODS =================

  isOverdue(row: any): boolean {
    if (!row || !row.dueDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dueDate = new Date(row.dueDate);
    dueDate.setHours(0, 0, 0, 0);

    return dueDate < today && row.state !== 'Completed';
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    setTimeout(() => {
      params.api.autoSizeAllColumns();
    }, 300);
  }

  selectTab(tab: 'MY' | 'TEAM' | 'CALENDAR'): void {
    this.selectedTab = tab;

    setTimeout(() => {
      if (this.gridApi) {
        if (tab === 'MY') {
          this.gridApi.setGridOption('rowData', this.mydata);
        } else if (tab === 'TEAM') {
          this.gridApi.setGridOption('rowData', this.teamsData);
        }
        this.gridApi.sizeColumnsToFit();
      }
    });
  }

  // ================= CALENDAR METHODS =================

  get currentMonthName(): string {
    return new Date(this.currentYear, this.currentMonth)
      .toLocaleString('default', { month: 'long' });
  }

  generateCalendar(): void {
    const firstDay = new Date(this.currentYear, this.currentMonth, 1);
    const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);

    const startDay = firstDay.getDay();
    const totalDays = lastDay.getDate();

    this.calendar = [];
    this.todayEvents = [];

    for (let i = 0; i < startDay; i++) {
      this.calendar.push({ date: null, events: [], isToday: false });
    }

    for (let day = 1; day <= totalDays; day++) {
      const dayEvents = this.calendarEvents.filter(e => {
        const eventDate = new Date(e.date);
        return eventDate.getDate() === day &&
               eventDate.getMonth() === this.currentMonth &&
               eventDate.getFullYear() === this.currentYear;
      });
      const isToday = day === this.todayDate && this.currentMonth === this.todayMonth && this.currentYear === this.todayYear;

      if (isToday) this.todayEvents = dayEvents;

      this.calendar.push({ date: day, events: dayEvents, isToday });
    }
  }

  previousMonth(): void {
    this.currentMonth--;
    if (this.currentMonth < 0) { this.currentMonth = 11; this.currentYear--; }
    this.generateCalendar();
  }

  nextMonth(): void {
    this.currentMonth++;
    if (this.currentMonth > 11) { this.currentMonth = 0; this.currentYear++; }
    this.generateCalendar();
  }

  get upcomingDeadlinesThisMonth() {
    return this.calendarEvents
      .filter(e => {
        const eventDate = new Date(e.date);
        return eventDate.getMonth() === this.currentMonth &&
               eventDate.getFullYear() === this.currentYear;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }

  formatStatusClass(status: string): string {
    return status?.replace(/\s+/g, '-').toLowerCase() || '';
  }

  onDownload(): void {
    if (!this.allActions || this.allActions.length === 0) {
      this.snackBar.open('No data available to export', 'OK', { duration: 3000 });
      return;
    }

    this.spinner.start();
    setTimeout(() => {
      this.spinner.stop();
      this.exportToCsv(this.allActions);
    }, 1000);
  }

  private exportToCsv(data: any[]): void {
    const headers = ['Action ID', 'Action Name', 'Originating From', 'Priority', 'State', 'Due Date', 'Assigned To', 'Deviation ID', 'Action Outcome'];
    const csvData = data.map(item => [
      item.actionId,
      item.actionName,
      item.originatingFrom,
      item.priority,
      item.state,
      item.dueDate,
      item.assignedTo,
      item.deviationId,
      item.actionOutcome
    ]);

    csvData.unshift(headers);

    const csvString = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `actions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

 refreshData(): void {
  this.snackBar.open('Refreshing data...', 'OK', { duration: 500 });
  
  this.loadActions(); 
}
  getDeviationUrl(params: any): string {
  const deviationId = params.rowData.deviationCode;

  const screenPath = this.userRoutingModule.print(
    'DeviationManagementComponent'
  );

  return this.router.serializeUrl(
    this.router.createUrlTree(
      ['/user/Menubar', screenPath, deviationId] // 👈 ID after path
    )
  );
}
}